import { Button, FieldLabel, Flex, Label } from "@braze/beacon-core";
import { Select } from "@braze/beacon-lab-select";
import styled from "styled-components";
import { CAMPAIGN_REGION, CAMPAIGN_SUBREGIONS, QUARTERS, STATIC_YEARS, YEARS } from "../../../constants/SelectConstants";
import { useEffect, useState } from "react";
import { AnalysisFiltersObject, MarketoProgramObject, ParentCampaignObject } from "../../../types/APITypes";
import api from "../../../util/axios";
import { formatOption } from '../../../util/optionsFormatter';
import { useGlobalAppContext } from "../../../contexts/GlobalAppContext";
import { useAnalysisContext } from "../../../contexts/AnalysisContext";


const getParentsOptions = async () => {
    return await api
    .get(`/parent-campaigns/get-parent-campaigns`)
}

export const AnalysisFilters = (() => {

    const { parentCampaigns, programs } = useGlobalAppContext();

    const { analysisCampaigns, analysisFilters, analysisLoading, setAnalysisFilters, setAnalysisRefresh, setAnalysisLoading } = useAnalysisContext();

    const handleApplyFilters = () => {
        setAnalysisRefresh(true);
        setAnalysisLoading(true);
    }

    const handleFilterChange = (key : keyof AnalysisFiltersObject) => {

        return (value : string[]) => {

            setAnalysisFilters({...analysisFilters, [key] : value});
        }
    }
    const handleSubregionFilterChange = () => {
        return (value: string[]) => {
            const normalizedValues = value.map((subregion) => subregion.toLowerCase());
            setAnalysisFilters({...analysisFilters, campaign_subregion: normalizedValues});
        }
    }

    const campaignNameOptions = analysisCampaigns.length ? formatOption(analysisCampaigns.map(c => c.campaign_name_final), false) : [];

    const campaignOwnerOptions = (():{"label":string, "value":string}[] => {
        let nameSet = new Set<string>();
        analysisCampaigns.map(c => nameSet.add(c.campaign_owner))
        let opts = formatOption(Array.from(nameSet).sort(), false);
        return opts
    })();

    return (
        <Flex direction="column">
            <FiltersFlex>
                <Flex direction="column" style={{width: "50%"}}>
                    <SelectFilter
                        title={"Fiscal Year"}
                        multi={true}
                        handleChange={handleFilterChange("fiscal_year")}
                        options={formatOption(STATIC_YEARS)}
                    />
                    <SelectFilter
                        title={"Fiscal Quarter"}
                        multi={true}
                        handleChange={handleFilterChange("fiscal_quarter")}
                        options={formatOption(QUARTERS, false)}
                    />
                </Flex>
                <Flex direction="column" style={{width: "50%"}}>
                    <SelectFilter
                        title={"Region"}
                        multi={true}
                        handleChange={handleFilterChange("campaign_region")}
                        options={formatOption(CAMPAIGN_REGION)}
                    />
                    <SelectFilter
                        title={"Sub Region"}
                        multi={true}
                        handleChange={handleSubregionFilterChange()}
                        options={formatOption(CAMPAIGN_SUBREGIONS)}
                    />

                </Flex>
                <Flex direction="column" style={{width: "80%"}}>
                    <SelectFilter
                        title={"Campaign Type"}
                        multi={true}
                        handleChange={handleFilterChange("campaign_type")}
                        options={formatOption(programs.map((p : MarketoProgramObject) => p['sfdc_type']), false)}
                    />
                    <SelectFilter
                        title={"Parent Campaign"}
                        multi={true}
                        handleChange={handleFilterChange("parent_campaign")}
                        options={formatOption(parentCampaigns.map((p : ParentCampaignObject) => p["parent_campaign_name"]), false)}
                    />
                </Flex>

                <Flex direction="column" style={{width: "100%"}}>
                    <SelectFilter
                        title={"Campaign Name"}
                        multi={true}
                        handleChange={handleFilterChange("campaign_name_final")}
                        options={campaignNameOptions}
                    />
                    <SelectFilter
                        title={"Campaign Owner"}
                        multi={true}
                        handleChange={handleFilterChange("campaign_owner")}
                        options={campaignOwnerOptions}
                    />
                </Flex>
            </FiltersFlex>
            <Flex style={{width: "100%", marginTop: "1rem",}} direction="column">
                <Label style={{fontSize: "16px", marginTop: "0.5rem", textAlign: "center", fontWeight: "normal"}}>
                    Results only refresh after clicking "Apply Filters"
                </Label>
                <Button onClick={handleApplyFilters} loadingState={analysisLoading ? "loading" : "idle"}>
                    Apply Filters
                </Button>
                <ProjectionsToggle/>

            </Flex>

        </Flex>

    )
});


const FiltersFlex = styled(Flex)`
        justify-content: space-between;

        > * {
            &:not(:last-of-type) {
            margin-right: 20px;
            }
        }
    `

type SelectProps = {
    title: string,
    multi: boolean,
    handleChange: Function,
    options: {label:string, value:string}[],
};

const ProjectionsToggle = () => {

    const {showProjections, setShowProjections} = useAnalysisContext();

    const onStyle = {
        background: "#FFA524",
        borderColor: "#FFA524",
    }
    const offStyle = {
        background: "#D8DCDE",
        borderColor: "#D8DCDE"
    }
    return (
        <ProjectionsToggleButtonsBox>
            <ToggleButton size="full" style={ showProjections === "live" ?  onStyle : offStyle}
                onClick={() => setShowProjections("live")}
            >
                Live Metrics
            </ToggleButton>
            <ToggleButton size="full" style={ showProjections === "projections" ?  onStyle : offStyle}
                onClick={() => setShowProjections("projections")}
            >
                Projections
            </ToggleButton>
            <ToggleButton size="full" style={ showProjections === "delta" ?  onStyle : offStyle}
                onClick={() => setShowProjections("delta")}
            >
                Delta/Variance
            </ToggleButton>
        </ProjectionsToggleButtonsBox>

    )

}
const SelectFilter = ({title, multi, handleChange, options} : SelectProps) => {


    return (
        <FilterContainer>
            <FieldLabel label={title}>
                <Select
                    fluid
                    options={options}
                    isMulti={multi}
                    isNoWrap={true}
                    onChange={(selectedOption: any) => {
                        let options: string[] = [];
                        selectedOption.forEach((element: { value: any; }) => {
                            options.push(element.value)
                        });
                        handleChange(options);
                    }
                }
                />

            </FieldLabel>

        </FilterContainer>
    );
};

const FilterContainer = styled(Flex)`

    width: 100%;
    display: flex;
    .bcl-field-label-container {
        width: 100%;
    }
`

const ProjectionsToggleButtonsBox = styled(Flex)`
    min-width: 100%;
    margin-top: 1rem;
    // justify-content: space-between;
`
const ToggleButton = styled(Button)`
    display: flex;
    margin-right: 0.2rem;
    // min-width: 100%;
    // background: #FFA524,
    // border-color: #FFA524
`
